"use client";

import { useEffect, useRef, useState } from "react";
import {
  createChart, CrosshairMode, LineStyle,
  type IChartApi, type ISeriesApi, type LogicalRange, type SeriesMarker, type Time,
} from "lightweight-charts";
import { FILL_LABEL, turnOf, type Row } from "@/lib/engine";
import { fmtEok, fmtNum, fmtPrice } from "@/lib/format";
import { Button } from "@/components/ui/button";

type Palette = Record<"up" | "down" | "tp" | "ma" | "pnl" | "text" | "muted" | "grid" | "bg" | "font", string>;

// 국내 HTS 관례: 상승·매수 = 빨강, 하락·매도 = 파랑
function readPalette(): Palette {
  const s = getComputedStyle(document.documentElement);
  const v = (k: string) => s.getPropertyValue(k).trim();
  return {
    up: v("--bt-up"), down: v("--bt-down"), tp: v("--bt-tp"), ma: v("--bt-ma"), pnl: v("--bt-pnl"),
    text: v("--bt-text"), muted: v("--bt-muted"), grid: v("--bt-grid"), bg: v("--bt-bg"),
    font: `${v("--font-plex-mono") || "ui-monospace"}, monospace`,
  };
}

const RANGES = [
  { key: "3m", label: "3개월", months: 3 },
  { key: "1y", label: "1년", months: 12 },
  { key: "3y", label: "3년", months: 36 },
  { key: "all", label: "전체", months: 0 },
] as const;

export function BacktestChart({ rows, maLen, lotsPerDay, showTurns, uw }: {
  rows: Row[]; maLen: number; lotsPerDay: number; showTurns: boolean;
  uw: { from: string | null; to: string | null } | null;
}) {
  const priceEl = useRef<HTMLDivElement>(null);
  const dispEl = useRef<HTMLDivElement>(null);
  const posEl = useRef<HTMLDivElement>(null);
  const pnlEl = useRef<HTMLDivElement>(null);
  const ddEl = useRef<HTMLDivElement>(null);
  const charts = useRef<IChartApi[]>([]);
  const [hover, setHover] = useState<Row | null>(null);
  const [range, setRange] = useState<(typeof RANGES)[number]["key"]>("1y");
  const [theme, setTheme] = useState(0);

  // 테마 변경(다크/라이트) 시 다시 그림
  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const bump = () => setTheme((t) => t + 1);
    mq.addEventListener("change", bump);
    const mo = new MutationObserver(bump);
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => { mq.removeEventListener("change", bump); mo.disconnect(); };
  }, []);

  useEffect(() => {
    if (!priceEl.current || !dispEl.current || !posEl.current || !pnlEl.current || !ddEl.current || !rows.length) return;
    const P = readPalette();
    const base = {
      layout: { background: { color: P.bg }, textColor: P.muted, fontFamily: P.font, fontSize: 11 },
      grid: { vertLines: { color: P.grid }, horzLines: { color: P.grid } },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderColor: P.grid, minimumWidth: 72 },
      timeScale: { borderColor: P.grid, rightOffset: 3 },
      localization: { locale: "ko-KR" },
      autoSize: true,
    };
    const t = (r: Row) => r.d as Time;

    const cPrice = createChart(priceEl.current, { ...base, timeScale: { ...base.timeScale, visible: false } });
    const candles = cPrice.addCandlestickSeries({
      upColor: P.up, downColor: P.down, borderUpColor: P.up, borderDownColor: P.down,
      wickUpColor: P.up, wickDownColor: P.down, priceFormat: { type: "price", precision: 2, minMove: 0.01 },
    });
    candles.setData(rows.map((r) => ({ time: t(r), open: r.o, high: r.h, low: r.l, close: r.c })));
    const maLine = cPrice.addLineSeries({
      color: P.ma, lineWidth: 2, priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false,
    });
    maLine.setData(rows.map((r) => r.ma == null ? { time: t(r) } : { time: t(r), value: r.ma }));

    // 수량이 일일 수량과 다를 때(한도·청산으로 줄어든 날)만 숫자 표시
    const qty = (q: number) => (Math.abs(q) === lotsPerDay ? "" : String(Math.abs(q)));
    const markers: SeriesMarker<Time>[] = [];
    for (const r of rows) {
      if (r.action === "BS") {
        markers.push({ time: t(r), position: "belowBar", shape: "arrowUp", color: P.up, text: `B${r.buys}` });
        markers.push({ time: t(r), position: "aboveBar", shape: "arrowDown", color: P.down, text: `S${r.sells}` });
      } else if (r.action === "B") markers.push({ time: t(r), position: "belowBar", shape: "arrowUp", color: P.up, text: qty(r.q) });
      else if (r.action === "S") markers.push({ time: t(r), position: "aboveBar", shape: "arrowDown", color: P.down, text: qty(r.q) });
      else if (r.action === "TP") markers.push({
        time: t(r), position: r.q < 0 ? "aboveBar" : "belowBar", shape: "circle", color: P.tp, text: `TP${qty(r.q)}`,
      });
      if (r.roll) markers.push({ time: t(r), position: "aboveBar", shape: "square", color: P.muted, text: "롤" });
    }
    candles.setMarkers(markers);

    // 이격도 = 종가 / MA × 100, 100 기준선. 익실현 사용 시 종가 기준 변곡점 표시
    const cDisp = createChart(dispEl.current, { ...base, timeScale: { ...base.timeScale, visible: false } });
    const disp = cDisp.addBaselineSeries({
      baseValue: { type: "price", price: 100 },
      topLineColor: P.up, bottomLineColor: P.down, lineWidth: 2,
      topFillColor1: `${P.up}30`, topFillColor2: `${P.up}08`,
      bottomFillColor1: `${P.down}08`, bottomFillColor2: `${P.down}30`,
      priceFormat: { type: "price", precision: 2, minMove: 0.01 },
      priceLineVisible: false,
    });
    disp.setData(rows.map((r) => r.disp == null ? { time: t(r) } : { time: t(r), value: r.disp }));
    disp.createPriceLine({ price: 100, color: P.muted, lineStyle: LineStyle.Dotted, lineWidth: 1, axisLabelVisible: true, title: "" });
    if (showTurns) {
      const turns: SeriesMarker<Time>[] = [];
      for (let k = 2; k < rows.length; k++) {
        const tn = turnOf(rows[k - 2].disp, rows[k - 1].disp, rows[k].disp);
        if (tn === 1) turns.push({ time: t(rows[k - 1]), position: "aboveBar", shape: "circle", color: P.tp });
        else if (tn === -1) turns.push({ time: t(rows[k - 1]), position: "belowBar", shape: "circle", color: P.tp });
      }
      disp.setMarkers(turns);
    }

    const cPos = createChart(posEl.current, { ...base, timeScale: { ...base.timeScale, visible: false } });
    const posSeries = cPos.addHistogramSeries({ priceLineVisible: false, priceFormat: { type: "volume" } });
    posSeries.setData(rows.map((r) => ({ time: t(r), value: r.pos, color: r.pos >= 0 ? P.up : P.down })));

    const cPnl = createChart(pnlEl.current, { ...base, timeScale: { ...base.timeScale, visible: false } });
    const pnl: ISeriesApi<"Baseline"> = cPnl.addBaselineSeries({
      baseValue: { type: "price", price: 0 },
      topLineColor: P.up, bottomLineColor: P.down, lineWidth: 2,
      topFillColor1: `${P.up}38`, topFillColor2: `${P.up}0a`,
      bottomFillColor1: `${P.down}0a`, bottomFillColor2: `${P.down}38`,
      priceFormat: { type: "custom", formatter: (v: number) => `${v.toFixed(1)}억`, minMove: 0.1 },
      priceLineVisible: false,
    });
    pnl.setData(rows.map((r) => ({ time: t(r), value: r.cum / 1e8 })));
    pnl.createPriceLine({ price: 0, color: P.muted, lineStyle: LineStyle.Dotted, lineWidth: 1, axisLabelVisible: false });

    // 언더워터: 누적손익 고점 대비 낙폭. 최장 언더워터 구간의 시작·회복 지점 표시
    const cDd = createChart(ddEl.current, base);
    const dd = cDd.addAreaSeries({
      lineColor: P.down, lineWidth: 1, topColor: `${P.down}10`, bottomColor: `${P.down}55`,
      invertFilledArea: true,
      priceFormat: { type: "custom", formatter: (v: number) => `${v.toFixed(1)}억`, minMove: 0.1 },
      priceLineVisible: false,
    });
    dd.setData(rows.map((r) => ({ time: t(r), value: r.dd / 1e8 })));
    if (uw?.from) {
      const m: SeriesMarker<Time>[] = [{ time: uw.from as Time, position: "aboveBar", shape: "arrowDown", color: P.text, text: "최장 언더워터 시작" }];
      if (uw.to) m.push({ time: uw.to as Time, position: "aboveBar", shape: "arrowDown", color: P.text, text: "회복" });
      dd.setMarkers(m);
    }

    const all = [cPrice, cDisp, cPos, cPnl, cDd];
    charts.current = all;
    const seriesOf = new Map<IChartApi, ISeriesApi<"Candlestick" | "Histogram" | "Baseline">>([
      [cPrice, candles], [cDisp, disp], [cPos, posSeries], [cPnl, pnl], [cDd, dd as unknown as ISeriesApi<"Baseline">],
    ]);
    const valueOf = (o: IChartApi, r: Row) =>
      o === cPrice ? r.c : o === cDisp ? r.disp : o === cPos ? r.pos : o === cPnl ? r.cum / 1e8 : r.dd / 1e8;
    const byTime = new Map(rows.map((r) => [r.d, r]));

    // 시간축·크로스헤어 동기화
    let syncing = false;
    for (const c of all) {
      c.timeScale().subscribeVisibleLogicalRangeChange((lr: LogicalRange | null) => {
        if (syncing || !lr) return;
        syncing = true;
        for (const o of all) if (o !== c) o.timeScale().setVisibleLogicalRange(lr);
        syncing = false;
      });
      c.subscribeCrosshairMove((param) => {
        const r = param.time ? byTime.get(param.time as string) ?? null : null;
        setHover(r);
        for (const o of all) {
          if (o === c) continue;
          if (r && valueOf(o, r) != null) o.setCrosshairPosition(valueOf(o, r)!, param.time!, seriesOf.get(o)!);
          else o.clearCrosshairPosition();
        }
      });
    }
    applyRange(all, rows, range, false);
    return () => { all.forEach((c) => c.remove()); charts.current = []; };
    // range는 버튼에서 직접 적용하므로 의존성에서 제외
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, theme, lotsPerDay, showTurns, uw?.from, uw?.to]);

  const r = (hover && rows.find((row) => row.d === hover.d)) || rows[rows.length - 1];
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Legend r={r} maLen={maLen} live={!!hover} />
        <div className="flex gap-1">
          {RANGES.map((x) => (
            <Button key={x.key} size="sm" variant={range === x.key ? "secondary" : "ghost"}
              onClick={() => { setRange(x.key); applyRange(charts.current, rows, x.key, true); }}>
              {x.label}
            </Button>
          ))}
        </div>
      </div>
      <Pane label={`수정연결 일봉 · 당일 확정 ${maLen}일선 · 타점`} className="h-[360px]" ref={priceEl} />
      <Pane label={`이격도 (종가 / ${maLen}일선 × 100)${showTurns ? " · ● 변곡점" : ""}`} className="h-[120px]" ref={dispEl} />
      <Pane label="보유 계약 (＋롱 / −숏)" className="h-[120px]" ref={posEl} />
      <Pane label="누적 손익 (억원)" className="h-[160px]" ref={pnlEl} />
      <Pane label="언더워터 · 고점 대비 낙폭 (억원)" className="h-[130px]" ref={ddEl} />
    </div>
  );
}

// 최근 날짜 기준 달력 n개월 전부터 끝까지를 딱 맞게. 세로축 자동 스케일도 다시 켠다.
function targetRange(rows: Row[], key: string) {
  const months = RANGES.find((x) => x.key === key)?.months ?? 0;
  const n = rows.length;
  if (!months) return { from: -0.5, to: n - 0.5 };
  const last = new Date(rows[n - 1].d);
  last.setMonth(last.getMonth() - months);
  const cut = last.toISOString().slice(0, 10);
  const from = Math.max(0, rows.findIndex((r) => r.d > cut));
  return { from: from - 0.5, to: n - 0.5 };
}

function applyRange(all: IChartApi[], rows: Row[], key: string, animate: boolean) {
  if (!all.length || !rows.length) return;
  for (const c of all) {
    c.priceScale("right").applyOptions({ autoScale: true });
    c.timeScale().applyOptions({ rightOffset: 0 });
  }
  const to = targetRange(rows, key);
  const start = all[0].timeScale().getVisibleLogicalRange();
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (!animate || !start || reduce) {
    all.forEach((c) => c.timeScale().setVisibleLogicalRange(to));
    return;
  }
  // 현재 범위 → 목표 범위로 부드럽게 줌 (첫 차트만 움직이면 나머지는 동기화 핸들러가 따라옴)
  const t0 = performance.now();
  const dur = 380;
  const ease = (x: number) => 1 - (1 - x) ** 3;
  const step = (now: number) => {
    const k = ease(Math.min(1, (now - t0) / dur));
    all[0].timeScale().setVisibleLogicalRange({
      from: start.from + (to.from - start.from) * k,
      to: start.to + (to.to - start.to) * k,
    });
    if (k < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

function Pane({ label, className, ref }: { label: string; className: string; ref: React.Ref<HTMLDivElement> }) {
  return (
    <div className="relative rounded-lg border bg-[var(--bt-bg)]">
      <span className="pointer-events-none absolute top-1.5 left-2.5 z-10 text-[11px] tracking-wide text-muted-foreground">{label}</span>
      <div ref={ref} className={className} />
    </div>
  );
}

const ACTION_LABEL = { B: "매수", S: "매도", BS: "매수·매도", TP: "익실현" } as const;

function Legend({ r, maLen, live }: { r: Row; maLen: number; live: boolean }) {
  const chg = r.c - r.pc;
  return (
    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 font-mono text-xs tabular-nums">
      <span className="font-sans font-medium text-foreground">{r.d}{!live && <span className="ml-1 text-muted-foreground">(최근)</span>}</span>
      <span>시 {fmtPrice(r.o)} 고 {fmtPrice(r.h)} 저 {fmtPrice(r.l)} 종 <b className={chg >= 0 ? "text-[var(--bt-up)]" : "text-[var(--bt-down)]"}>{fmtPrice(r.c)}</b></span>
      <span style={{ color: "var(--bt-ma)" }}>당일 확정 MA{maLen} {r.ma == null ? "–" : fmtPrice(r.ma)}</span>
      <span className="text-muted-foreground">신호 {r.signalDate} {r.signalPhase === "open" ? "시가" : "마감"} · 판단 기준 {r.referenceDate} {r.signalRef == null ? "–" : fmtPrice(r.signalRef, 6)}</span>
      <span className="text-muted-foreground">이격도 {r.disp?.toFixed(2) ?? "–"}</span>
      <span>
        {r.action ? (
          <b className={r.action === "B" ? "text-[var(--bt-up)]" : r.action === "S" ? "text-[var(--bt-down)]" : "text-[var(--bt-tp)]"}>
            {ACTION_LABEL[r.action]} {r.q > 0 ? "+" : ""}{fmtNum(r.q)}
          </b>
        ) : <span className="text-muted-foreground">관망</span>}
        {r.action && <span className="text-muted-foreground"> @ {fmtPrice(r.fill)} {FILL_LABEL[r.fillSrc]}{r.buys && r.sells ? ` (매수 ${r.buys}·매도 ${r.sells})` : ""}</span>}
      </span>
      <span>보유 {fmtNum(r.pos)}</span>
      <span className={r.pnl >= 0 ? "text-[var(--bt-up)]" : "text-[var(--bt-down)]"}>일손익 {fmtEok(r.pnl, 2)}</span>
      <span className="text-muted-foreground">누적 {fmtEok(r.cum, 1)} · 낙폭 {r.dd < 0 ? fmtEok(r.dd, 1) : "고점"}</span>
    </div>
  );
}
